import React, {RefObject} from 'react'
import Scroll from 'react-scroll'
import equal from 'fast-deep-equal'
import {HideInPC} from '../../../styledComponents/ResponsiveConvenience'
import { Fab, Zoom } from '@mui/material';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded'

interface ExternalProps {
}

export const withScrollToTop = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps>,
    option: {
        visibleTop: number,
        visibleBottom: number,
    },
) => {

    type ResultProps = OriginalProps & ExternalProps;

    interface State {
        showGoToTopButton: boolean
    }

    return class extends React.Component<ResultProps, State> {
        static displayName: string = `withScrollToTop(${WrappedComponent.displayName})`
        goToTopButtonRef: RefObject<HTMLButtonElement>

        constructor(props: Readonly<ResultProps>) {
            super(props)
            this.handleClickGoToTop = this.handleClickGoToTop.bind(this)
            this.handleScroll = this.handleScroll.bind(this)
            this.goToTopButtonRef = React.createRef()
            this.state = {
                showGoToTopButton: false,
            }
        }

        componentDidMount() : void{
            window.addEventListener('scroll', this.handleScroll, {passive: true, capture: false})
        }

        componentWillUnmount():void{
            window.removeEventListener('scroll', this.handleScroll)
        }

        handleScroll(): void {
            const scrollTop = getScrollTop()
            const {scrollHeight, clientHeight} = document.documentElement
            const {visibleTop, visibleBottom} = option
            const bottom = scrollHeight - clientHeight - scrollTop
            const visible = (scrollTop > visibleTop && bottom > visibleBottom)

            this.setState({
                showGoToTopButton: visible,
            })
        }

        handleClickGoToTop(): void {
            Scroll.animateScroll.scrollToTop({
                delay: 100,
                duration: 500,
                smooth: true,
            })
        }

        shouldComponentUpdate(
            nextProps: Readonly<ResultProps>, nextState: Readonly<State>, _: any,
        ): boolean {
            return !(equal(nextProps, this.props) && equal(nextState, this.state))
        }

        render(): JSX.Element {
            return <><WrappedComponent {...this.props} />
                <HideInPC>
                    <GoToTopFloatingActionButton
                        in={this.state.showGoToTopButton}
                        onClick={this.handleClickGoToTop}
                    />
                </HideInPC>
            </>
        }
    }
}

interface GoToTopFloatingActionButtonProps {
    in: boolean
    onClick?: () => void
    children?: never
}

const GoToTopFloatingActionButton: React.FC<GoToTopFloatingActionButtonProps> = props => {
    return <Zoom
        in={props.in}
        timeout={500}
        style={{
            transitionDelay: `0ms`,
        }}
        unmountOnExit
    >
        <Fab
            size="medium"
            disableFocusRipple
            style={{
                color: '#FFFFFF',
                backgroundColor: 'rgba(80, 80, 80, 0.75)',
                position: 'fixed',
                bottom: '30px',
                right: '5px',
            }}
            onClick={props.onClick}
        >
            <PublishRoundedIcon fontSize={'large'}/>
        </Fab>
    </Zoom>
}

const getScrollTop = (): number => {
    return Math.max(
        window.pageYOffset,
        document.documentElement.scrollTop,
        document.body.scrollTop)
}
